import React, { useState } from "react";
import { Formik, Form, Field, FieldArray, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { addSupplier, updateSupplier } from "../../redux/features/supplier.features";
import {
  Button, Row, Col, Table, Badge, Spinner, Tabs, Tab
} from "react-bootstrap";
import {
  Save, Plus, Trash2, Building2, User, Phone,
  Mail, Globe, MapPin, Landmark, CreditCard,
  ShieldCheck, Users, Tag, Layers, FileText
} from "lucide-react";
import { toast } from "react-toastify";

const defaultInitialValues = {
  business_name: "",
  contact_person: "",
  address: {
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    country: "Sri Lanka",
    postal_code: "",
  },
  contacts: [],
  contact_info: {
    contact_number: "",
    alternate_contact_number: "",
    email: "",
    website: "",
  },
  tax_info: {
    tax_number: "",
    registration_number: "",
  },
  financial: {
    opening_balance: 0,
    current_balance: 0,
    credit_limit: 0,
    credit_period: "due_on_receipt",
    payment_terms: "",
    custom_credit_period: 0,
    credit_segments: []
  },
  bank_details: {
    bank_name: "",
    bank_account_number: "",
    bank_routing_number: "",
    SWIFT_code: "",
  },
  logo_image: "",
  notes: "",
};

const SupplierForm = ({ initialValues = defaultInitialValues, isEdit = false, savedCompleted, setShow }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("identity");

  const validationSchema = Yup.object({
    business_name: Yup.string().required("Required"),
    contact_person: Yup.string().required("Required"),
    address: Yup.object({
      address_line1: Yup.string().required("Required"),
      city: Yup.string().required("Required"),
      country: Yup.string().required("Required"),
    }),
    contact_info: Yup.object({
      contact_number: Yup.string().required("Required"),
    }),
  });

  const handleSubmit = (values, { setSubmitting }) => {
    const processedValues = { ...values };
    if (processedValues.financial?.credit_segments) {
      processedValues.financial.credit_segments = processedValues.financial.credit_segments.map(seg => ({
        ...seg,
        agent_id: (seg.agent_id && seg.agent_id.length > 5) ? seg.agent_id : undefined
      }));
    }

    const action = isEdit ? updateSupplier(processedValues) : addSupplier(processedValues);

    dispatch(action)
      .then((res) => {
        if (res.error) {
          toast.error(res.payload?.message || res.error.message || "Operation failed");
        } else {
          toast.success("Success");
          if (savedCompleted) savedCompleted(res.payload);
          if (setShow) setShow(false);
        }
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div className="erp-supplier-form bg-white">
      <style>
        {`
          .erp-supplier-form .nav-tabs {
            background: #f1f5f9;
            border-bottom: 2px solid #e2e8f0;
            padding: 10px 10px 0;
            border-radius: 12px 12px 0 0;
            gap: 4px;
          }
          .erp-supplier-form .nav-link {
            background: transparent;
            border: none;
            color: #64748b;
            font-weight: 700;
            font-size: 13px;
            padding: 12px 24px;
            border-radius: 8px 8px 0 0;
            display: flex;
            align-items: center;
            gap: 8px;
            transition: all 0.2s;
          }
          .erp-supplier-form .nav-link.active {
            background: #fff;
            color: #0f172a;
            box-shadow: 0 -2px 10px rgba(0,0,0,0.03);
          }
          .erp-label {
            display: block;
            font-size: 11px;
            font-weight: 800;
            color: #475569;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 8px;
          }
          .erp-input {
            width: 100%;
            background: #fff;
            border: 1px solid #cbd5e1;
            border-radius: 8px;
            padding: 10px 12px 10px 40px;
            font-size: 14px;
            font-weight: 500;
            color: #1e293b;
            transition: all 0.2s;
          }
          .erp-input:focus {
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
            outline: none;
          }
          .erp-card {
            background: #fff;
            border: 1px solid #e2e8f0;
            border-radius: 12px;
            padding: 24px;
            height: 100%;
          }
          .erp-section-title {
            font-size: 14px;
            font-weight: 800;
            color: #0f172a;
            display: flex;
            align-items: center;
            gap: 10px;
            margin-bottom: 20px;
            padding-bottom: 12px;
            border-bottom: 1px solid #f1f5f9;
          }
          .btn-erp-primary {
            background: #0f172a;
            color: #fff;
            border: none;
            padding: 12px 32px;
            border-radius: 8px;
            font-weight: 700;
            font-size: 14px;
            display: flex;
            align-items: center;
            gap: 10px;
            transition: all 0.2s;
            cursor: pointer;
          }
          .btn-erp-primary:hover:not(:disabled) {
            background: #1e293b;
            transform: translateY(-1px);
          }
        `}
      </style>

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ errors, values, isSubmitting, setFieldValue }) => (
          <Form>
            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>

              {/* TAB 1: IDENTITY */}
              <Tab eventKey="identity" title={<><Building2 size={16} /> Profile</>}>
                <div className="p-4">
                  <Row className="g-4">
                    <Col lg={8}>
                      <div className="erp-card">
                        <div className="erp-section-title">
                          <Building2 size={18} className="text-slate-400" />
                          Business Identity
                        </div>
                        <Row className="g-3 mb-4">
                          <Col md={12}>
                            <label className="erp-label">Business Name</label>
                            <div className="position-relative">
                              <Building2 size={18} className="position-absolute" style={{ left: '12px', top: '12px', color: '#94a3b8' }} />
                              <Field name="business_name" className="erp-input" placeholder="Enter company name" />
                            </div>
                            <ErrorMessage name="business_name" component="div" className="text-danger small mt-1" />
                          </Col>
                          <Col md={12}>
                            <label className="erp-label">Lead Contact Person</label>
                            <div className="position-relative">
                              <User size={18} className="position-absolute" style={{ left: '12px', top: '12px', color: '#94a3b8' }} />
                              <Field name="contact_person" className="erp-input" placeholder="Primary representative" />
                            </div>
                          </Col>
                        </Row>

                        <div className="erp-section-title">
                          <MapPin size={18} className="text-slate-400" />
                          Location & Logistics
                        </div>

                        <Row className="g-3">
                          <Col md={12}>
                            <label className="erp-label">Official Address</label>
                            <div className="position-relative">
                              <MapPin size={18} className="position-absolute" style={{ left: '12px', top: '12px', color: '#94a3b8' }} />
                              <Field name="address.address_line1" className="erp-input" placeholder="Street layout" />
                            </div>
                            <ErrorMessage name="address.address_line1" component="div" className="text-danger small" />
                          </Col>
                          <Col md={6}>
                            <label className="erp-label">City</label>
                            <div className="position-relative">
                              <MapPin size={18} className="position-absolute" style={{ left: '12px', top: '12px', color: '#94a3b8' }} />
                              <Field name="address.city" className="erp-input" placeholder="City" />
                            </div>
                            <ErrorMessage name="address.city" component="div" className="text-danger small" />
                          </Col>
                          <Col md={6}>
                            <label className="erp-label">Country</label>
                            <div className="position-relative">
                              <MapPin size={18} className="position-absolute" style={{ left: '12px', top: '12px', color: '#94a3b8' }} />
                              <Field name="address.country" placeholder="Country" className="erp-input" />
                            </div>
                            <ErrorMessage name="address.country" component="div" className="text-danger small" />
                          </Col>
                        </Row>
                      </div>
                    </Col>

                    <Col lg={4}>
                      <div className="erp-card bg-slate-50">
                        <div className="erp-section-title">
                          <Phone size={18} className="text-slate-400" />
                          Communication
                        </div>
                        <div className="mb-3">
                          <label className="erp-label">Phone</label>
                          <div className="position-relative">
                            <Phone size={16} className="position-absolute" style={{ left: '12px', top: '12px', color: '#94a3b8' }} />
                            <Field name="contact_info.contact_number" className="erp-input" />
                          </div>
                        </div>
                        <div className="mb-3">
                          <label className="erp-label">Email</label>
                          <div className="position-relative">
                            <Mail size={16} className="position-absolute" style={{ left: '12px', top: '12px', color: '#94a3b8' }} />
                            <Field name="contact_info.email" className="erp-input" />
                          </div>
                        </div>
                        <hr className="my-4" />
                        <label className="erp-label">Strategic Notes</label>
                        <Field as="textarea" name="notes" rows={6} className="form-control border-slate-200 text-sm" style={{ borderRadius: '8px' }} />
                      </div>
                    </Col>
                  </Row>
                </div>
              </Tab>

              {/* TAB 2: FINANCE */}
              <Tab eventKey="finance" title={<><CreditCard size={16} /> Finance</>}>
                <div className="p-4">
                  <Row className="g-4">
                    <Col lg={6}>
                      <div className="erp-card">
                        <div className="erp-section-title">
                          <CreditCard size={18} className="text-slate-400" />
                          Credit Policy
                        </div>
                        <div className="mb-4">
                          <label className="erp-label">Global Credit Limit</label>
                          <div className="position-relative">
                            <Tag size={18} className="position-absolute" style={{ left: '12px', top: '12px', color: '#94a3b8' }} />
                            <Field name="financial.credit_limit" type="number" className="erp-input" style={{ fontSize: '20px', fontWeight: '700' }} />
                          </div>
                        </div>
                        <Row className="g-3">
                          <Col md={6}>
                            <label className="erp-label">Period Type</label>
                            <Field as="select" name="financial.credit_period" className="form-select erp-input" style={{ paddingLeft: '12px' }}>
                              <option value="due_on_receipt">On Receipt</option>
                              <option value="net30">Net 30</option>
                              <option value="custom">Custom</option>
                            </Field>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                    <Col lg={6}>
                      <div className="erp-card">
                        <div className="erp-section-title">
                          <Landmark size={18} className="text-slate-400" />
                          Settlement A/C
                        </div>
                        <div className="mb-3">
                          <label className="erp-label">Bank Name</label>
                          <Field name="bank_details.bank_name" className="erp-input" style={{ paddingLeft: '12px' }} />
                        </div>
                        <div className="mb-3">
                          <label className="erp-label">A/C Number</label>
                          <Field name="bank_details.bank_account_number" className="erp-input" style={{ paddingLeft: '12px' }} />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Tab>

              {/* TAB 3: QUOTAS */}
              <Tab eventKey="quotas" title={<><Layers size={16} /> Quotas</>}>
                <div className="p-4">
                  <div className="erp-card">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <div className="erp-section-title mb-0 border-0 p-0">
                        <Layers size={18} className="text-slate-400" />
                        Brand Specific Limits
                      </div>
                      <FieldArray name="financial.credit_segments">
                        {({ push }) => (
                          <Button variant="outline-primary" size="sm" onClick={() => push({ name: "", limit: 0, utilized: 0, agent_id: "" })}>
                            <Plus size={14} className="me-1" /> Add Segment
                          </Button>
                        )}
                      </FieldArray>
                    </div>

                    <FieldArray name="financial.credit_segments">
                      {({ remove }) => (
                        <Row className="g-3">
                          {values.financial?.credit_segments?.map((seg, index) => (
                            <Col md={12} key={index}>
                              <div className="p-3 bg-slate-50 border rounded-3 d-flex gap-3 align-items-end">
                                <div className="flex-grow-1">
                                  <label className="erp-label">Brand Name</label>
                                  <Field name={`financial.credit_segments[${index}].name`} className="erp-input" style={{ paddingLeft: '12px' }} />
                                </div>
                                <div style={{ width: '200px' }}>
                                  <label className="erp-label">Credit Limit (LKR)</label>
                                  <Field name={`financial.credit_segments[${index}].limit`} type="number" className="erp-input" style={{ paddingLeft: '12px' }} />
                                </div>
                                <div style={{ width: '250px' }}>
                                  <label className="erp-label">Assigned Agent</label>
                                  <Field as="select" name={`financial.credit_segments[${index}].agent_id`} className="form-select erp-input" style={{ paddingLeft: '12px' }}>
                                    <option value="">Direct</option>
                                    {values.contacts?.map((c, i) => (
                                      <option key={i} value={c._id || ""}>{c.name || `Registry #${i + 1}`}</option>
                                    ))}
                                  </Field>
                                </div>
                                <Button variant="link" className="text-danger p-2 mb-1" onClick={() => remove(index)}>
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      )}
                    </FieldArray>
                  </div>
                </div>
              </Tab>

              {/* TAB 4: AGENTS */}
              <Tab eventKey="agents" title={<><Users size={16} /> Agents</>}>
                <div className="p-4">
                  <div className="erp-card">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <div className="erp-section-title mb-0 border-0 p-0">
                        <Users size={18} className="text-slate-400" />
                        Representatives
                      </div>
                      <FieldArray name="contacts">
                        {({ push }) => (
                          <Button variant="outline-dark" size="sm" onClick={() => push({ name: "", designation: "", phone: "", email: "", isPrimary: false })}>
                            <Plus size={14} className="me-1" /> Add Personnel
                          </Button>
                        )}
                      </FieldArray>
                    </div>

                    <FieldArray name="contacts">
                      {({ remove }) => (
                        <div className="table-responsive">
                          <Table borderless hover className="text-sm align-middle">
                            <thead className="bg-slate-50">
                              <tr className="border-bottom">
                                <th className="p-2  " style={{ marginBottom: 0 }}>Full Name</th>
                                <th className="p-2 " style={{ marginBottom: 0 }}>Role</th>
                                <th className="p-2 " style={{ marginBottom: 0 }}>Communication</th>
                                <th className="p-2 w-10"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {values.contacts?.map((agent, index) => (
                                <tr key={index} className="border-bottom">
                                  <td className="p-2"><Field name={`contacts[${index}].name`} className="erp-input" style={{ paddingLeft: '10px' }} /></td>
                                  <td className="p-2"><Field name={`contacts[${index}].designation`} className="erp-input" style={{ paddingLeft: '10px' }} /></td>
                                  <td className="p-2">
                                    <div className="d-flex gap-2">
                                      <Field name={`contacts[${index}].phone`} className="erp-input" style={{ paddingLeft: '10px' }} placeholder="Phone" />
                                      <Field name={`contacts[${index}].email`} className="erp-input" style={{ paddingLeft: '10px' }} placeholder="Email" />
                                    </div>
                                  </td>
                                  <td className="text-center">
                                    <Button variant="link" className="text-danger p-0" onClick={() => remove(index)}>
                                      <Trash2 size={16} />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </FieldArray>
                  </div>
                </div>
              </Tab>
            </Tabs>

            <div className="p-4 bg-slate-50 d-flex justify-content-end gap-3 border-top">
              <Button variant="link" onClick={() => setShow?.(false)} className="text-slate-500 fw-bold uppercase text-decoration-none">
                Discard
              </Button>
              <button type="submit" disabled={isSubmitting} className="btn-erp-primary">
                {isSubmitting ? <Spinner size="sm" /> : <Save size={18} />}
                {isEdit ? "Update Registry" : "Commit Supplier"}
              </button>
            </div>

            {Object.keys(errors).length > 0 && (
              <div className="text-danger small px-4 pb-2">
                Please fix errors in: {Object.keys(errors).join(", ")}
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default SupplierForm;
